import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { filter, map, Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { NotificationService } from './notifications.service';
import { BaseService } from './base.service';
import { KeycloakService } from 'keycloak-angular';
import { Message } from '../components/layout/chat-sidebar/components/chat-message/chat-message.interface';
import { TMessageUpdatedSocket } from '../components/layout/chat-sidebar/components/chat/chat.interface';
import { ChatTimelineSocket } from '../interfaces/timeline.interface';
import {
  ISocketIntegrationEvent,
  IViberSocketError,
} from '../components/layout/right-sidebar/components/integration-instagram/integration.interface';
import { IException } from '../interfaces';
import { BOARD_SOCKET_EVENTS } from '../components/board-v3/board-v3-socket-events';
import { FiscalNotificationData } from './fiscal-notification.service';

interface SocketContext {
  companyId?: string | number;
  userId?: string | number;
}

export interface ManagerStatusEvent {
  manager_id: string;
  status: 'online' | 'offline';
  company_id: string;
  last_active_at?: string;
}

export interface ChannelAccessChangedEvent {
  channel_ids?: string[];
}

export interface FiscalOperationNotification extends FiscalNotificationData {
  company_id: string;
  fiscal_operation_id: string;
  cash_register_id: string;
  status: string;
}

type NotificationSocketEvent = [
  id: string,
  title: string,
  message: string,
  source: string,
  type: string,
  context?: FiscalOperationNotification,
];

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket!: Socket;
  private readonly connectedSubject = new Subject<void>();
  private readonly connectionRestoredSubject = new Subject<void>();
  private wasDisconnected = false;

  constructor(
    private authService: AuthService,
    private keycloakService: KeycloakService,
    private baseService: BaseService,
    private notificationService: NotificationService,
  ) {
    if (!this.socket?.connected)
      this.socket = io(`${environment.socketURL}`, {
        query: this.createSocketQuery(),
        path: '/api/ws/socket.io',
      });
    this.registerConnectionLifecycle();
  }

  public connected$(): Observable<void> {
    return this.connectedSubject.asObservable();
  }

  public connectionRestored$(): Observable<void> {
    return this.connectionRestoredSubject.asObservable();
  }

  public channelAccessChanged(): Observable<ChannelAccessChangedEvent | undefined> {
    return this.fromSocketEvent<ChannelAccessChangedEvent | undefined>('channel:access-changed');
  }

  public notification() {
    return this.fromSocketEvent<string>('notification:get');
  }

  public notificationV2(): Observable<NotificationSocketEvent> {
    return this.fromSocketEventArgs<NotificationSocketEvent>('notification');
  }

  public fiscalOperationCompleted(): Observable<FiscalOperationNotification> {
    return this.notificationV2().pipe(
      map((event) => event[5]),
      filter((context): context is FiscalOperationNotification =>
        context?.kind === 'fiscal_operation' && !!context.fiscal_operation_id &&
        ['succeeded', 'skipped', 'manual_required'].includes(context.status) &&
        context.company_id === String(this.baseService.company?.id)),
    );
  }

  delayedMessages(): Observable<string> {
    return this.fromSocketEvent<string>('messages:delayed:update');
  }

  public getUpdateMessage(): Observable<any> {
    return this.fromSocketEvent<string>('message:update');
  }

  public getUpdateFunnel(): Observable<any> {
    return this.fromSocketEvent<string>('funnel:update');
  }

  public onErrorOlx(channel_id: string): Observable<any> {
    return this.fromSocketEvent<string>(`olx:${channel_id}:error`);
  }

  public offErrorOlx(channel_id: string): void {
    const event = `olx:${channel_id}:error`;
    this.socket.off(event);
  }

  /** Emitted to the company room whenever an employee goes online/offline */
  public onManagerStatus(): Observable<ManagerStatusEvent> {
    return this.fromSocketEvent<ManagerStatusEvent>('manager:status');
  }

  public onSendStatusOlx(channelId: string): Observable<any> {
    return this.fromSocketEvent<string>(`olx:${channelId}:status`);
  }

  public offSuccessOlx(channel_id: string): void {
    const event = `olx:${channel_id}:status`;
    this.socket.off(event);
  }

  public getNewMessage() {
    return this.fromSocketEvent<{ chat: any; message: Message; reactionAdded: boolean; error: IException }>(
      'message:new',
    );
  }

  public getQRCodeRead() {
    return this.fromSocketEvent('qrcode:read');
  }

  public tariffPaymentCompleted(): Observable<any[]> {
    return this.fromSocketEventArgs<any[]>('tariff-payment-completed');
  }

  public reloadWindow(): Observable<void> {
    return this.fromSocketEventVoid('reload-window');
  }

  public ping() {
    this.socket.emit('ping');
  }

  public chatTimelineEvents() {
    return this.fromSocketEvent<ChatTimelineSocket>('chat:timeline:new');
  }

  public messageUpdated() {
    return this.fromSocketEvent<TMessageUpdatedSocket>('message:updated');
  }

  public instAuthAction() {
    return this.fromSocketEvent<ISocketIntegrationEvent>('inst:auth');
  }

  public facebookAuthAction() {
    return this.fromSocketEvent<ISocketIntegrationEvent>('facebook:auth');
  }

  public viberChannelAction() {
    return this.fromSocketEvent<ISocketIntegrationEvent>('viber:channel');
  }

  public viberError() {
    return this.fromSocketEvent<IViberSocketError>('viber:error');
  }

  public whatsAppChannelAction() {
    return this.fromSocketEvent<ISocketIntegrationEvent>('whatsapp:channel');
  }

  public openPayment(): Observable<any> {
    return this.fromSocketEvent<IViberSocketError>('payment:open');
  }

  public promChannelAction() {
    return this.fromSocketEvent<ISocketIntegrationEvent>('prom:channel');
  }

  public boardCardUpdated() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.cardUpdate);
  }

  public cardDetailUpdated() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.cardDetailUpdate);
  }

  public boardColumnsUpdated() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.funnelColumnsUpdate);
  }

  public boardFieldsUpdated() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.funnelFieldsUpdate);
  }

  public funnelCreated(): Observable<unknown> {
    return this.fromSocketEvent<unknown>(BOARD_SOCKET_EVENTS.funnelCreate);
  }

  public funnelUpdated(): Observable<unknown> {
    return this.fromSocketEvent<unknown>(BOARD_SOCKET_EVENTS.funnelUpdate);
  }

  public funnelDeleted(): Observable<unknown> {
    return this.fromSocketEvent<unknown>(BOARD_SOCKET_EVENTS.funnelDelete);
  }

  public cardCreated() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.cardCreate);
  }

  public cardDeleted() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.cardDelete);
  }

  public presencePong() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.presencePong);
  }

  public cardPresencePong() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.cardPresencePong);
  }

  public subscribeFunnel(funnelId: string): void {
    const context = this.getSocketContext();

    this.emitIfConnected(BOARD_SOCKET_EVENTS.funnelSubscribe, {
      funnel_id: funnelId,
      company_id: context.companyId,
      ...(context.userId ? { user_id: context.userId } : {}),
    });
  }

  public unsubscribeFunnel(funnelId: string): void {
    const context = this.getSocketContext();

    this.emitIfConnected(BOARD_SOCKET_EVENTS.funnelUnsubscribe, {
      funnel_id: funnelId,
      company_id: context.companyId,
    });
  }

  public subscribeCard(cardId: string, companyId: string, funnelId?: string, observeOnly = false): void {
    const context = this.getSocketContext();

    this.emitIfConnected(BOARD_SOCKET_EVENTS.cardSubscribe, {
      ...(observeOnly ? { observe_only: true } : {}),
      card_id: cardId,
      company_id: companyId,
      ...(context.userId ? { user_id: context.userId } : {}),
      ...(funnelId ? { funnel_id: funnelId } : {}),
    });
  }

  public unsubscribeCard(cardId: string, companyId: string, funnelId?: string, keepRoom = false): void {
    this.emitIfConnected(BOARD_SOCKET_EVENTS.cardUnsubscribe, {
      ...(keepRoom ? { keep_room: true } : {}),
      card_id: cardId,
      company_id: companyId,
      ...(funnelId ? { funnel_id: funnelId } : {}),
    });
  }

  public pingFunnelPresence(funnelId: string): void {
    const context = this.getSocketContext();

    this.emitIfConnected(BOARD_SOCKET_EVENTS.presencePing, {
      funnel_id: funnelId,
      company_id: context.companyId,
      ...(context.userId ? { user_id: context.userId } : {}),
    });
  }

  public pingCardPresence(cardId: string, companyId: string, funnelId?: string): void {
    const context = this.getSocketContext();

    this.emitIfConnected(BOARD_SOCKET_EVENTS.cardPresencePing, {
      card_id: cardId,
      company_id: companyId,
      ...(context.userId ? { user_id: context.userId } : {}),
      ...(funnelId ? { funnel_id: funnelId } : {}),
    });
  }

  public cardPresence() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.cardPresence);
  }

  public funnelPresence() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.funnelPresence);
  }

  public importProgress() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.funnelImportProgress);
  }

  public exportProgress() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.funnelExportProgress);
  }

  public leadConversionResult() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.leadConversionResult);
  }

  public timelineEvent() {
    return this.fromSocketEvent<any>(BOARD_SOCKET_EVENTS.cardTimelineEvent);
  }

  private registerConnectionLifecycle(): void {
    this.socket.io.on('reconnect_attempt', () => {
      this.socket.io.opts.query = this.createSocketQuery();
    });

    this.socket.on('disconnect', () => {
      this.wasDisconnected = true;
    });

    this.socket.on('connect', () => {
      this.connectedSubject.next();

      if (!this.wasDisconnected) return;

      this.wasDisconnected = false;
      this.connectionRestoredSubject.next();
    });
  }

  private emitIfConnected(event: string, payload: unknown): void {
    if (!this.socket.connected) return;

    this.socket.emit(event, payload);
  }

  private createSocketQuery(): Record<string, string | number | undefined> {
    const context = this.getSocketContext();

    return {
      token: this.keycloakService.getKeycloakInstance().token,
      company_id: context.companyId,
      user_id: context.userId,
    };
  }

  private getSocketContext(): SocketContext {
    const company = this.baseService.getCompanyObj() as { id?: string | number };
    const user = this.baseService.currentUserInfo as { id?: string | number } | undefined;

    return {
      companyId: company.id,
      userId: user?.id,
    };
  }

  private fromSocketEvent<T = any>(event: string): Observable<T> {
    return new Observable<T>(observer => {
      const handler = (data: T) => {
        observer.next(data);
      };
      this.socket.on(event, handler);

      return () => this.socket.off(event, handler);
    });
  }

  private fromSocketEventArgs<T extends unknown[] = unknown[]>(event: string): Observable<T> {
    return new Observable<T>(observer => {
      const handler = (...data: T) => {
        observer.next(data);
      };
      this.socket.on(event, handler);

      return () => this.socket.off(event, handler);
    });
  }

  private fromSocketEventVoid(event: string): Observable<void> {
    return new Observable<void>(observer => {
      const handler = () => {
        observer.next();
      };
      this.socket.on(event, handler);

      return () => this.socket.off(event, handler);
    });
  }
}
